import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { adminApi } from "@/lib/api";
import {
  Plus,
  Trash2,
  X,
  Save,
  ChevronLeft,
  ChevronRight,
  Wrench,
  AlertTriangle,
  Heart,
  Palmtree,
  Calendar as CalendarIcon,
  ClipboardCheck,
} from "lucide-react";

const EVENT_TYPES = ["besiktning", "skada", "sjukdom", "semester", "annat"];

const TYPE_COLOR = {
  besiktning: { bg: "bg-blue-500", text: "text-blue-300", border: "border-blue-500", icon: ClipboardCheck },
  skada: { bg: "bg-red-500", text: "text-red-300", border: "border-red-500", icon: Wrench },
  sjukdom: { bg: "bg-orange-500", text: "text-orange-300", border: "border-orange-500", icon: Heart },
  semester: { bg: "bg-emerald-500", text: "text-emerald-300", border: "border-emerald-500", icon: Palmtree },
  annat: { bg: "bg-zinc-500", text: "text-zinc-300", border: "border-zinc-500", icon: AlertTriangle },
};

// Format a JS Date as YYYY-MM-DD using LOCAL components (avoid UTC timezone shift).
const fmtISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const parseISO = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

// ISO 8601 week number (Monday-first, week 1 contains the year's first Thursday).
const getISOWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const isInRange = (day, startStr, endStr) => {
  const dayStr = fmtISO(day);
  const start = startStr;
  const end = endStr || startStr;
  return dayStr >= start && dayStr <= end;
};

const emptyEvent = {
  title: "",
  event_type: "besiktning",
  start_date: fmtISO(new Date()),
  end_date: "",
  assignee: "",
  notes: "",
};

export default function AdminCalendar({ adminKey }) {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(today);

  const client = adminApi(adminKey);

  const load = async () => {
    setLoading(true);
    try {
      const res = await client.get("/calendar");
      setEvents(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunde inte ladda");
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    setInitialized(true);
    load();
  }

  const goPrev = () => {
    let y = viewYear;
    let m = viewMonth - 1;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
    setViewYear(y);
    setViewMonth(m);
  };
  const goNext = () => {
    let y = viewYear;
    let m = viewMonth + 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
    setViewYear(y);
    setViewMonth(m);
  };
  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedDate(today);
  };

  // Build grid: weeks of dates (Mon-Sun)
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  // JS weekday: 0=Sun..6=Sat. Convert to 0=Mon..6=Sun
  const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
  const gridStart = new Date(firstOfMonth);
  gridStart.setDate(firstOfMonth.getDate() - firstWeekday);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push(d);
  }

  const eventsOnDay = (d) =>
    events.filter((e) => isInRange(d, e.start_date, e.end_date));

  const selectedDayEvents = eventsOnDay(selectedDate);

  const startNew = () => {
    setEditing({
      ...emptyEvent,
      start_date: fmtISO(selectedDate),
    });
  };
  const startEdit = (ev) => setEditing({ ...ev, end_date: ev.end_date || "" });
  const cancel = () => {
    setEditing(null);
    setError("");
  };

  const update = (k) => (e) =>
    setEditing({ ...editing, [k]: e.target.value });

  const save = async () => {
    setError("");
    try {
      const payload = {
        title: editing.title,
        event_type: editing.event_type,
        start_date: editing.start_date,
        end_date: editing.end_date || null,
        assignee: editing.assignee || null,
        notes: editing.notes || null,
      };
      if (editing.id) {
        await client.patch(`/calendar/${editing.id}`, payload);
      } else {
        await client.post("/calendar", payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Kunde inte spara");
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(t.admin.calendar.confirm_delete)) return;
    await client.delete(`/calendar/${ev.id}`);
    await load();
  };

  const monthLabel = `${t.admin.calendar.months[viewMonth]} ${viewYear}`;
  const isCurrentMonth = (d) => d.getMonth() === viewMonth;

  const fmtDay = (d) => d.toLocaleDateString("sv-SE", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  return (
    <div data-testid="admin-calendar-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl md:text-4xl uppercase">
            {t.admin.calendar.title}
          </h2>
          <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
            {t.admin.calendar.lead}
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="admin-calendar-add-btn"
          className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#ff7a30] transition-colors"
        >
          <Plus size={14} /> {t.admin.calendar.add}
        </button>
      </div>

      {/* Type legend */}
      <div className="flex flex-wrap gap-3 mb-6">
        {EVENT_TYPES.map((type) => {
          const cfg = TYPE_COLOR[type];
          const Icon = cfg.icon;
          return (
            <div
              key={type}
              className="inline-flex items-center gap-2 text-[10px] font-mono tracking-widest uppercase text-zinc-400 border border-zinc-800 px-3 py-1.5"
            >
              <span className={`w-2.5 h-2.5 ${cfg.bg}`} />
              <Icon size={12} />
              {t.admin.calendar.types[type]}
            </div>
          );
        })}
      </div>

      {error && (
        <div className="mb-4 border border-red-600 bg-red-600/10 p-3 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar grid */}
        <div className="lg:col-span-2 border border-zinc-800 bg-[#0d0d10]">
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <button
              onClick={goPrev}
              data-testid="admin-calendar-prev"
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
              title={t.admin.calendar.prev}
            >
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-3">
              <div
                className="font-display text-2xl uppercase tracking-wide"
                data-testid="admin-calendar-month-label"
              >
                {monthLabel}
              </div>
              <button
                onClick={goToday}
                data-testid="admin-calendar-today"
                className="text-[10px] font-mono tracking-widest uppercase border border-zinc-700 px-2 py-1 hover:border-[#ff5a00] hover:text-[#ff5a00]"
              >
                {t.admin.calendar.today}
              </button>
            </div>
            <button
              onClick={goNext}
              data-testid="admin-calendar-next"
              className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-900 transition-colors"
              title={t.admin.calendar.next}
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Weekday header (with week-number column on left) */}
          <div className="grid grid-cols-[44px_repeat(7,1fr)] border-b border-zinc-800">
            <div className="p-2 text-center text-[9px] font-mono tracking-widest text-zinc-600 border-r border-zinc-800">
              V
            </div>
            {t.admin.calendar.weekdays_short.map((wd, i) => (
              <div
                key={i}
                className="p-2 text-center text-[10px] font-mono tracking-widest text-zinc-500 border-l border-zinc-800 first:border-l-0"
              >
                {wd}
              </div>
            ))}
          </div>

          {/* Days grid — 6 weekly rows, each prefixed with week number */}
          <div>
            {Array.from({ length: 6 }).map((_, weekIdx) => {
              const weekDays = cells.slice(weekIdx * 7, weekIdx * 7 + 7);
              const weekNo = getISOWeek(weekDays[0]);
              return (
                <div
                  key={weekIdx}
                  className="grid grid-cols-[44px_repeat(7,1fr)]"
                  data-testid={`admin-calendar-week-${weekNo}`}
                >
                  <div className="flex items-center justify-center text-[10px] font-mono tracking-widest text-zinc-500 border-t border-r border-zinc-800 bg-[#0a0a0c]">
                    {weekNo}
                  </div>
                  {weekDays.map((d, i) => {
                    const dayEvents = eventsOnDay(d);
                    const isSelected = isSameDay(d, selectedDate);
                    const isToday = isSameDay(d, today);
                    const otherMonth = !isCurrentMonth(d);
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedDate(d)}
                        data-testid={`admin-calendar-day-${fmtISO(d)}`}
                        className={`relative min-h-[68px] md:min-h-[88px] p-2 text-left border-l border-t border-zinc-800 first:border-l-0 transition-colors ${
                          otherMonth ? "bg-[#0a0a0c]" : ""
                        } ${
                          isSelected
                            ? "bg-zinc-900 ring-2 ring-[#ff5a00] ring-inset z-10"
                            : "hover:bg-zinc-900/60"
                        }`}
                      >
                        <div
                          className={`text-xs font-mono ${
                            otherMonth
                              ? "text-zinc-700"
                              : isToday
                              ? "text-[#ff5a00] font-bold"
                              : "text-zinc-300"
                          }`}
                        >
                          {String(d.getDate()).padStart(2, "0")}
                        </div>
                        <div className="mt-1.5 space-y-1">
                          {dayEvents.slice(0, 3).map((ev) => {
                            const cfg = TYPE_COLOR[ev.event_type] || TYPE_COLOR.annat;
                            return (
                              <div
                                key={ev.id}
                                className={`h-1.5 w-full ${cfg.bg}`}
                                title={`${t.admin.calendar.types[ev.event_type]}: ${ev.title}`}
                              />
                            );
                          })}
                          {dayEvents.length > 3 && (
                            <div className="text-[9px] font-mono text-zinc-500">
                              +{dayEvents.length - 3}
                            </div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected day detail */}
        <aside className="border border-zinc-800 bg-[#0d0d10]">
          <div className="p-4 border-b border-zinc-800">
            <div className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">
              {t.admin.calendar.title}
            </div>
            <div className="font-display text-xl uppercase mt-1 leading-tight">
              {fmtDay(selectedDate)}
            </div>
          </div>
          <div className="p-4">
            {loading ? (
              <div className="text-zinc-500 font-mono text-sm">
                {t.common.loading}
              </div>
            ) : selectedDayEvents.length === 0 ? (
              <div className="text-zinc-500 text-sm font-mono py-8 text-center">
                {t.admin.calendar.empty_day}
              </div>
            ) : (
              <ul className="space-y-3">
                {selectedDayEvents.map((ev) => {
                  const cfg = TYPE_COLOR[ev.event_type] || TYPE_COLOR.annat;
                  const Icon = cfg.icon;
                  return (
                    <li
                      key={ev.id}
                      data-testid={`admin-calendar-event-${ev.id}`}
                      className={`border-l-2 ${cfg.border} pl-3 pr-2 py-2 bg-zinc-900/50`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div
                            className={`text-[10px] font-mono tracking-widest uppercase ${cfg.text} flex items-center gap-1.5`}
                          >
                            <Icon size={11} />
                            {t.admin.calendar.types[ev.event_type]}
                          </div>
                          <div className="font-display text-base uppercase truncate mt-0.5">
                            {ev.title}
                          </div>
                          {ev.assignee && (
                            <div className="text-xs text-zinc-400 mt-1">
                              {ev.assignee}
                            </div>
                          )}
                          {ev.end_date && ev.end_date !== ev.start_date && (
                            <div className="text-[10px] font-mono text-zinc-500 mt-1">
                              {ev.start_date} → {ev.end_date}
                            </div>
                          )}
                          {ev.notes && (
                            <div className="text-xs text-zinc-400 mt-2 italic line-clamp-3">
                              {ev.notes}
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            onClick={() => startEdit(ev)}
                            data-testid={`admin-calendar-edit-${ev.id}`}
                            className="text-[10px] font-mono tracking-widest uppercase border border-zinc-700 px-2 py-0.5 hover:border-[#ff5a00] hover:text-[#ff5a00]"
                          >
                            {t.admin.calendar.edit}
                          </button>
                          <button
                            onClick={() => remove(ev)}
                            data-testid={`admin-calendar-delete-${ev.id}`}
                            className="text-zinc-500 hover:text-red-400 p-1"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>

      {/* Modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          data-testid="admin-calendar-modal"
        >
          <div className="bg-[#0d0d10] border border-zinc-800 max-w-xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <div className="font-display text-2xl uppercase flex items-center gap-2">
                <CalendarIcon size={20} className="text-[#ff5a00]" />
                {editing.id ? "Redigera" : t.admin.calendar.add}
              </div>
              <button onClick={cancel} className="text-zinc-500 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 gap-5">
              <div>
                <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                  {t.admin.calendar.fields.event_type} *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
                  {EVENT_TYPES.map((type) => {
                    const cfg = TYPE_COLOR[type];
                    const Icon = cfg.icon;
                    const active = editing.event_type === type;
                    return (
                      <button
                        type="button"
                        key={type}
                        onClick={() => setEditing({ ...editing, event_type: type })}
                        data-testid={`admin-calendar-type-${type}`}
                        className={`flex items-center gap-2 px-3 py-2 text-xs font-mono tracking-widest uppercase border transition-colors ${
                          active
                            ? `${cfg.border} ${cfg.bg} text-black font-bold`
                            : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                        }`}
                      >
                        <Icon size={14} />
                        {t.admin.calendar.types[type]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                  {t.admin.calendar.fields.title} *
                </label>
                <input
                  type="text"
                  data-testid="admin-calendar-title"
                  className="brut-input"
                  value={editing.title}
                  onChange={update("title")}
                  placeholder="t.ex. Besiktning Scania, Michael semester..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                    {t.admin.calendar.fields.start_date} *
                  </label>
                  <input
                    type="date"
                    data-testid="admin-calendar-start-date"
                    className="brut-input"
                    value={editing.start_date}
                    onChange={update("start_date")}
                  />
                </div>
                <div>
                  <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                    {t.admin.calendar.fields.end_date}
                  </label>
                  <input
                    type="date"
                    data-testid="admin-calendar-end-date"
                    className="brut-input"
                    value={editing.end_date || ""}
                    onChange={update("end_date")}
                    min={editing.start_date}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                  {t.admin.calendar.fields.assignee}
                </label>
                <input
                  type="text"
                  data-testid="admin-calendar-assignee"
                  className="brut-input"
                  value={editing.assignee || ""}
                  onChange={update("assignee")}
                  placeholder={t.admin.calendar.fields.assignee_placeholder}
                />
              </div>

              <div>
                <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                  {t.admin.calendar.fields.notes}
                </label>
                <textarea
                  data-testid="admin-calendar-notes"
                  className="brut-input"
                  value={editing.notes || ""}
                  onChange={update("notes")}
                  rows={4}
                />
              </div>
            </div>

            {error && (
              <div className="px-6 pb-2 text-sm text-red-400">{error}</div>
            )}

            <div className="p-6 border-t border-zinc-800 flex gap-3 justify-end">
              <button
                onClick={cancel}
                className="border border-zinc-700 text-white px-5 py-3 text-xs tracking-widest uppercase hover:border-zinc-500"
              >
                {t.admin.calendar.cancel}
              </button>
              <button
                onClick={save}
                data-testid="admin-calendar-save-btn"
                disabled={!editing.title || !editing.start_date}
                className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-widest uppercase font-bold hover:bg-[#ff7a30] disabled:opacity-50"
              >
                <Save size={14} /> {t.admin.calendar.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
